import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import { QRCodeCanvas } from 'qrcode.react';

interface Tier {
  id: number;
  threshold: number;
  buzhu_amount: number;
  dangzheng_amount: number;
  per_person_reward: number;
  gifts: string[];
  claimed: boolean;
}

export default function InvitePage() {
  const navigate = useNavigate();
  const [refCode, setRefCode] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [approvedInvites, setApprovedInvites] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [notices, setNotices] = useState<{ id: number; title: string; type: string; html?: string; image_url?: string }[]>([]);
  const [showNotice, setShowNotice] = useState(false);
  const [noticeIndex, setNoticeIndex] = useState(0);

  useEffect(() => {
    api.get('/me').then(({ data: u }) => {
      const code = u?.referral_code || null;
      setRefCode(code);
      setInviteUrl(`${window.location.origin}/register?invite=${code ?? ''}`);
    }).catch(() => { });
    api.get('/invite-reward-tiers').then(({ data }) => {
      setApprovedInvites(data?.current_invites ?? 0);
      setTotalEarnings(data?.total_earnings ?? 0);
      setTiers(Array.isArray(data?.tiers) ? data.tiers : []);
    }).catch(() => setApprovedInvites(0));
    api.get('/notices', { params: { page: 'invite' } }).then(({ data }) => {
      const arr = Array.isArray(data) ? data : [];
      setNotices(arr);
      if (arr.length) { setNoticeIndex(0); setShowNotice(true); }
    }).catch(() => { });
  }, []);

  const copyText = async (text: string, label: string) => {
    if (!text) { toast.error(`暂无${label}`); return; }
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); toast.success(`${label}已复制`); return; }
      throw new Error('unavailable');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        const ok = document.execCommand('copy'); document.body.removeChild(ta);
        if (ok) toast.success(`${label}已复制`); else toast.error('复制失败');
      } catch { toast.error('复制失败'); }
    }
  };

  return (
    <div className="max-w-md mx-auto min-h-screen relative bg-[url(/images/invite_beijing.png)] bg-no-repeat bg-[length:100%_100%] pb-[130px] pt-[270px]">
      <AuthGuard />

      {/* Full background */}
      <div className="relative">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div> 

      {/* QR code & invite section */}
      <div className="px-4 mt-2 relative z-10">
        <div className="bg-[url(/images/invite_yaoqing.png)] bg-no-repeat bg-[length:100%_100%] rounded-xl p-4 relative overflow-hidden pt-[50px]"> 
          <div className="flex justify-center gap-4">
            {/* QR Code */}
            <div className="flex flex-col items-center justify-center text-center gap-1">
              <div className="bg-white p-1.5 rounded-lg shadow-sm">
                <QRCodeCanvas value={inviteUrl ?? ''} size={80} bgColor="#fff" fgColor="#000" />
              </div>
              <span className="text-[8px] text-[#8b0000] font-medium w-full">分享二维码</span>
            </div>

              {/* Invite code & link */}
              <div className="gap-1 flex flex-col justify-center text-center"> 
                  <span className="text-md text-[#3b0000] mb-1">我的邀请码: {refCode }</span>
                  <button
                    onClick={() => copyText(refCode ?? '', '邀请码')} 
                  >
                    <img src={"/images/invite_yaoqingma.png"} alt="" className="w-[200px] inline-block mr-1" />
                  </button>  
                  <button
                    onClick={() => copyText(inviteUrl ?? '', '邀请链接')} 
                  >
                    <img src={"/images/invite_lianjie.png"} alt="" className="w-[200px] inline-block mr-1" />
                  </button>
              </div>  
            </div>
        </div>
      </div>

      <div className="px-4 mt-2 relative z-10" onClick={() => navigate('/shenji')}>
        <img src="/images/invite_lingqu.png" alt="" className="w-full" /> 
      </div> 

      {/* Tier rewards */}
      <div className="mx-4 mt-2 relative z-10 bg-[url(/images/invite_fuli.png)] bg-no-repeat bg-[length:100%_100%] rounded-xl px-4 pt-[40px] pb-4"> 
        <div className="space-y-2">
          {tiers.map((tier) => {
            const reached = approvedInvites >= tier.threshold;
            return (
              <div key={tier.id} className="flex items-center gap-1 rounded-xl px-3 py-1 bg-[url(/images/invite_haoyou.png)] bg-no-repeat bg-[length:100%_100%] pl-[65px]"> 
                <div className="flex-1 min-w-0">
                  <p className="text-[#5a2d0c] text-xs font-bold">邀请{tier.threshold}位好友以上</p>
                  <p className="text-[#5a2d0c] text-xs -mt-0.5">
                    现金奖励变更为 <span className="text-[#c4161c] text-lg font-bold">{tier.per_person_reward}</span><span className="text-[#c4161c] text-xs font-bold">元/人</span>
                  </p>
                </div>
                <div className={`flex flex-col items-center justify-center rounded-full w-[60px] h-[40px] flex-shrink-0 ${reached ? 'bg-[#c4161c]' : 'bg-[#e8a87c]'}`}>
                  <span className="text-white text-xs font-bold">{approvedInvites}/{tier.threshold}</span>
                  <span className="text-white/80 text-[10px]">{reached ? '已达标' : '未达标'}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-3 text-center text-xs">
        <img src={"/images/invite_chengwei.png"} alt="" className="w-[300px] mx-auto" />
      </div>
      
      {/* Notice modal */}
      {showNotice && notices.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-w-md w-[90%] rounded-xl bg-white shadow max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b shrink-0">
              <div className="text-md">{notices[noticeIndex]?.title ?? ''}</div>
            </div>
            <div className="p-3 overflow-y-auto">
              {(() => {
                const n = notices[noticeIndex];
                return n?.type === 'image' && n?.image_url
                  ? <img src={n.image_url} alt="notice" className="w-full h-auto rounded" />
                  : <div dangerouslySetInnerHTML={{ __html: n?.html || '' }} />;
              })()}
            </div>
            <div className="p-2 flex justify-center border-t mt-3 shrink-0">
              <button className="text-md text-gray-500" onClick={() => {
                const next = noticeIndex + 1;
                if (next < notices.length) setNoticeIndex(next);
                else setShowNotice(false);
              }}>
                {noticeIndex + 1 < notices.length ? '下一条' : '关闭'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
